// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { ERROR_CODE_MAPPING } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import type { WellFormedError } from '@/api/types';
import {
  UpdateLineItemsInput,
  updateLineItems,
} from '@/billing/api/update-line-items';
import { invalidateInvoiceQueries } from '@/billing/hooks/invalidate-invoice-queries';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface UseUpdateLineItemsOptions {
  invoiceKey: string;
  onSuccess?: () => void;
  onError?: (apiError: WellFormedError) => void;
}

// Domain validation codes the line-items update endpoint emits. Callers
// surface these inline, so they should not be reported to Sentry.
const HANDLED_API_STATUS_CODES = new Set<number>([
  ERROR_CODE_MAPPING.invoice_mixed_currency,
]);

export function useUpdateLineItems(options: UseUpdateLineItemsOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateLineItemsInput) => updateLineItems(input),
    onSuccess: async () => {
      await invalidateInvoiceQueries({
        queryClient,
        invoiceKey: options.invoiceKey,
        includeCharges: true,
      });
      options.onSuccess?.();
    },
    onError: (error: AxiosError) => {
      const apiError = handleAPIError(error);
      const isHandled = HANDLED_API_STATUS_CODES.has(apiError.apiStatusCode);
      if (!isHandled && !apiError.sentToSentry) {
        Sentry.captureException(error);
      }
      options.onError?.(apiError);
    },
  });
}
